import type { Labelled } from './common.js';

/**
 * باني التقارير.
 *
 * محرّك استعلام عام يبنيه المستخدم بنفسه: يختار مصدر البيانات، وما يُقاس،
 * وبأي تقسيم، وبأي شكل. البديل عشرات التقارير الجاهزة التي لا تُغني عن
 * الحادي والعشرين الذي يحتاجه العميل ولم نتوقعه.
 *
 * كل ما يمكن اختياره معرَّف هنا حرفًا بحرف.
 *
 * المحرّك يبني جملة SQL، والجداول والأعمدة فيها تأتي من هذا الكتالوج لا من
 * الطلب. لو قُبل اسم عمود من المستخدم لصار الباب مفتوحًا لقراءة ما لا يخصه
 * أو لكسر الاستعلام. القيم وحدها هي ما يُمرَّر كوسائط.
 */

/* ==========================================================================
   الأنواع
   ========================================================================== */

/** كيف يُعرض التقرير. */
export const CHART_KINDS = ['TABLE', 'BAR', 'LINE', 'PIE'] as const;
export type ChartKind = (typeof CHART_KINDS)[number];

export interface ChartKindDef extends Labelled<ChartKind> {
  hint: string;
}

export const CHART_KIND_DEFS: readonly ChartKindDef[] = [
  { key: 'TABLE', label: 'جدول', kind: 'neutral', hint: 'أرقام مرتَّبة، تصلح للمراجعة والتصدير.' },
  {
    key: 'BAR',
    label: 'أعمدة',
    kind: 'info',
    hint: 'مقارنة فئات ببعضها: أي مصدر أفضل، أي مندوب أعلى.',
  },
  {
    key: 'LINE',
    label: 'خط زمني',
    kind: 'success',
    hint: 'تتبّع التغيّر عبر الزمن. يحتاج تقسيمًا بالتاريخ.',
  },
  {
    key: 'PIE',
    label: 'قطاعات',
    kind: 'warning',
    hint: 'نصيب كل فئة من الكل. يصلح لعدد قليل من الفئات.',
  },
] as const;

export const CHART_KIND_MAP = Object.fromEntries(
  CHART_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ChartKind, ChartKindDef>>;

/** كيف يُقسَّم التاريخ حين يكون هو محور التقرير. */
export const DATE_GRAINS = ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'] as const;
export type DateGrain = (typeof DATE_GRAINS)[number];

export const DATE_GRAIN_LABELS: Readonly<Record<DateGrain, string>> = {
  DAY: 'يومي',
  WEEK: 'أسبوعي',
  MONTH: 'شهري',
  QUARTER: 'ربع سنوي',
  YEAR: 'سنوي',
};

/** الوحدة التي تُعرض بها القيمة. */
export type ValueFormat = 'NUMBER' | 'MONEY' | 'PERCENT' | 'DAYS';

/* ==========================================================================
   عناصر المصدر
   ========================================================================== */

export type DimensionKind = 'ENUM' | 'REF' | 'DATE' | 'TEXT' | 'BOOLEAN';

export interface DimensionDef {
  key: string;
  label: string;
  kind: DimensionKind;
  /** اسم العمود في جدول المصدر. */
  column: string;
  /** للمراجع: الجدول والعمود اللذان يُقرأ منهما الاسم المعروض. */
  refTable?: string;
  refLabel?: string;
  /** للقوائم المغلقة: ترجمة المفتاح إلى نص عربي. */
  labels?: Readonly<Record<string, string>>;
  /**
   * مفتاح داخل عمود JSON، للحقول المخصَّصة.
   *
   * وجوده يعني أن `column` عمود JSON وأن القيمة تُقرأ بهذا المفتاح. وهو
   * الموضع الوحيد في الكتالوج الذي تأتي قيمته من بيانات المستأجر لا من
   * الشيفرة، فيُمرَّر وسيطًا في الاستعلام ولا يُدرَج في نصّه.
   */
  jsonKey?: string;
}

export type MeasureKind = 'COUNT' | 'SUM' | 'AVG' | 'MIN' | 'MAX';

export interface MeasureDef {
  key: string;
  label: string;
  kind: MeasureKind;
  /** العمود المُجمَّع. غير مطلوب مع COUNT. */
  column?: string;
  format: ValueFormat;
}

export interface FilterDef {
  key: string;
  label: string;
  kind: 'ENUM' | 'REF' | 'BOOLEAN';
  column: string;
  labels?: Readonly<Record<string, string>>;
  refTable?: string;
  refLabel?: string;
}

export interface SourceDef {
  key: string;
  label: string;
  description: string;
  /** اسم الجدول في قاعدة البيانات. */
  table: string;
  /** العمود الزمني الذي يُقيَّد به نطاق التقرير. */
  dateColumn: string;
  /** شرط ثابت يُضاف دائمًا، لاستبعاد الملغى مثلًا. */
  baseWhere?: string;
  dimensions: readonly DimensionDef[];
  measures: readonly MeasureDef[];
  filters: readonly FilterDef[];
}

/* ==========================================================================
   ترجمات القوائم المغلقة
   ========================================================================== */

const LEAD_STATUS: Record<string, string> = {
  NEW: 'جديد',
  CONTACTED: 'تم التواصل',
  WORKING: 'قيد العمل',
  QUALIFIED: 'مؤهَّل',
  CONVERTED: 'محوَّل',
  UNQUALIFIED: 'غير مؤهَّل',
  JUNK: 'مهمَل',
};

const LEAD_RATING: Record<string, string> = { HOT: 'أولوية عالية', WARM: 'أولوية متوسطة', COLD: 'أولوية منخفضة' };

const DEAL_STAGE: Record<string, string> = {
  CONTACT: 'تواصل',
  QUALIFICATION: 'تأهيل',
  PROPOSAL: 'عرض سعر',
  NEGOTIATION: 'تفاوض',
  CLOSING: 'إغلاق',
};

const DEAL_RESULT: Record<string, string> = { OPEN: 'مفتوحة', WON: 'مكسوبة', LOST: 'مخسورة' };

const QUOTATION_STATUS: Record<string, string> = {
  DRAFT: 'مسودة',
  SENT: 'تم الإرسال',
  NEGOTIATION: 'قيد المتابعة',
  ACCEPTED: 'مقبول',
  REJECTED: 'مرفوض',
  EXPIRED: 'منتهي الصلاحية',
};

const INVOICE_STATUS: Record<string, string> = {
  DRAFT: 'مسودة',
  SENT: 'مُرسَلة',
  PARTIAL: 'مسدَّدة جزئيًا',
  PAID: 'مسدَّدة بالكامل',
  OVERDUE: 'متأخرة',
  CANCELLED: 'ملغاة',
};

const BILL_STATUS: Record<string, string> = {
  DRAFT: 'مسودة',
  RECEIVED: 'مستلمة',
  PARTIAL: 'مسدَّدة جزئيًا',
  PAID: 'مسدَّدة بالكامل',
  OVERDUE: 'متأخرة',
  CANCELLED: 'ملغاة',
};

const ACTIVITY_TYPE: Record<string, string> = {
  TASK: 'مهمة',
  MEETING: 'اجتماع',
  CALL: 'مكالمة',
  MAIL: 'رسالة',
};

const ACTIVITY_STATUS: Record<string, string> = {
  PLANNED: 'مخطَّطة',
  DONE: 'منجَزة',
  CANCELLED: 'ملغاة',
};

const PRIORITY: Record<string, string> = { HIGH: 'مرتفعة', NORMAL: 'عادية', LOW: 'منخفضة' };

const ACCOUNT_KIND: Record<string, string> = { COMPANY: 'شركة', INDIVIDUAL: 'فرد' };

/* ==========================================================================
   المصادر
   ========================================================================== */

/** المستخدم المسؤول: مرجع متكرر في أغلب المصادر. */
const owner = (column = 'ownerId', label = 'المسؤول'): DimensionDef => ({
  key: column === 'ownerId' ? 'owner' : column,
  label,
  kind: 'REF',
  column,
  refTable: 'users',
  refLabel: 'name',
});

const account = (label = 'العميل'): DimensionDef => ({
  key: 'account',
  label,
  kind: 'REF',
  column: 'accountId',
  refTable: 'accounts',
  refLabel: 'name',
});

const COUNT: MeasureDef = { key: 'count', label: 'العدد', kind: 'COUNT', format: 'NUMBER' };

export const REPORT_SOURCES: readonly SourceDef[] = [
  {
    key: 'LEADS',
    label: 'العملاء المحتملون',
    description: 'قياس مصادر العملاء الجدد وجودتهم ومعدلات تحوّلهم.',
    table: 'leads',
    dateColumn: 'createdAt',
    dimensions: [
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: LEAD_STATUS },
      { key: 'rating', label: 'الأولوية', kind: 'ENUM', column: 'rating', labels: LEAD_RATING },
      { key: 'source', label: 'المصدر', kind: 'TEXT', column: 'source' },
      { key: 'city', label: 'المدينة', kind: 'TEXT', column: 'city' },
      { key: 'industry', label: 'النشاط', kind: 'TEXT', column: 'industry' },
      owner(),
      { key: 'createdAt', label: 'تاريخ التسجيل', kind: 'DATE', column: 'createdAt' },
    ],
    measures: [
      COUNT,
      { key: 'value', label: 'القيمة التقديرية', kind: 'SUM', column: 'value', format: 'MONEY' },
      { key: 'avgValue', label: 'متوسط القيمة', kind: 'AVG', column: 'value', format: 'MONEY' },
    ],
    filters: [
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: LEAD_STATUS },
      { key: 'rating', label: 'الأولوية', kind: 'ENUM', column: 'rating', labels: LEAD_RATING },
      {
        key: 'ownerId',
        label: 'المسؤول',
        kind: 'REF',
        column: 'ownerId',
        refTable: 'users',
        refLabel: 'name',
      },
    ],
  },

  {
    key: 'DEALS',
    label: 'الصفقات',
    description: 'حجم خط الأنابيب وتوزيعه على المراحل ونتائج الإغلاق.',
    table: 'deals',
    dateColumn: 'createdAt',
    dimensions: [
      { key: 'stage', label: 'المرحلة', kind: 'ENUM', column: 'stage', labels: DEAL_STAGE },
      { key: 'result', label: 'النتيجة', kind: 'ENUM', column: 'result', labels: DEAL_RESULT },
      { key: 'source', label: 'المصدر', kind: 'TEXT', column: 'source' },
      { key: 'lostReason', label: 'سبب الخسارة', kind: 'TEXT', column: 'lostReason' },
      owner(),
      account(),
      { key: 'createdAt', label: 'تاريخ الإنشاء', kind: 'DATE', column: 'createdAt' },
      { key: 'closedAt', label: 'تاريخ الإغلاق', kind: 'DATE', column: 'closedAt' },
    ],
    measures: [
      COUNT,
      { key: 'value', label: 'قيمة الصفقات', kind: 'SUM', column: 'value', format: 'MONEY' },
      { key: 'avgValue', label: 'متوسط القيمة', kind: 'AVG', column: 'value', format: 'MONEY' },
      {
        key: 'probability',
        label: 'متوسط الاحتمالية',
        kind: 'AVG',
        column: 'probability',
        format: 'PERCENT',
      },
    ],
    filters: [
      { key: 'stage', label: 'المرحلة', kind: 'ENUM', column: 'stage', labels: DEAL_STAGE },
      { key: 'result', label: 'النتيجة', kind: 'ENUM', column: 'result', labels: DEAL_RESULT },
      {
        key: 'ownerId',
        label: 'المسؤول',
        kind: 'REF',
        column: 'ownerId',
        refTable: 'users',
        refLabel: 'name',
      },
    ],
  },

  {
    key: 'QUOTATIONS',
    label: 'عروض الأسعار',
    description: 'ما عُرض على العملاء وما قُبل منه، ومعدل القبول.',
    table: 'quotations',
    dateColumn: 'issuedAt',
    dimensions: [
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: QUOTATION_STATUS },
      owner(),
      account(),
      { key: 'issuedAt', label: 'تاريخ الإصدار', kind: 'DATE', column: 'issuedAt' },
    ],
    measures: [
      COUNT,
      { key: 'total', label: 'إجمالي العروض', kind: 'SUM', column: 'total', format: 'MONEY' },
      { key: 'avgTotal', label: 'متوسط العرض', kind: 'AVG', column: 'total', format: 'MONEY' },
    ],
    filters: [
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: QUOTATION_STATUS },
      {
        key: 'ownerId',
        label: 'المسؤول',
        kind: 'REF',
        column: 'ownerId',
        refTable: 'users',
        refLabel: 'name',
      },
    ],
  },

  {
    key: 'INVOICES',
    label: 'فواتير العملاء',
    description: 'الإيراد المفوتر وحالته: ما حُصِّل وما تأخر.',
    table: 'invoices',
    dateColumn: 'issuedAt',
    // الملغاة ليست إيرادًا، وإدخالها في أي قياس يضخّم الأرقام بلا سبب.
    baseWhere: `status <> 'CANCELLED'`,
    dimensions: [
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: INVOICE_STATUS },
      owner(),
      account(),
      { key: 'issuedAt', label: 'تاريخ الإصدار', kind: 'DATE', column: 'issuedAt' },
      { key: 'dueAt', label: 'تاريخ الاستحقاق', kind: 'DATE', column: 'dueAt' },
    ],
    measures: [
      COUNT,
      { key: 'total', label: 'إجمالي الفواتير', kind: 'SUM', column: 'total', format: 'MONEY' },
      { key: 'paid', label: 'المحصَّل', kind: 'SUM', column: 'paidAmount', format: 'MONEY' },
      { key: 'tax', label: 'الضريبة', kind: 'SUM', column: 'taxAmount', format: 'MONEY' },
      { key: 'avgTotal', label: 'متوسط الفاتورة', kind: 'AVG', column: 'total', format: 'MONEY' },
    ],
    filters: [
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: INVOICE_STATUS },
      {
        key: 'ownerId',
        label: 'المسؤول',
        kind: 'REF',
        column: 'ownerId',
        refTable: 'users',
        refLabel: 'name',
      },
    ],
  },

  {
    key: 'PAYMENTS',
    label: 'التحصيلات',
    description: 'ما دخل فعلًا من العملاء، بأي طريقة وإلى أي خزينة.',
    table: 'payments',
    dateColumn: 'paidAt',
    dimensions: [
      { key: 'method', label: 'طريقة السداد', kind: 'TEXT', column: 'method' },
      account(),
      {
        key: 'cashAccount',
        label: 'الخزينة',
        kind: 'REF',
        column: 'cashAccountId',
        refTable: 'cash_accounts',
        refLabel: 'name',
      },
      { key: 'paidAt', label: 'تاريخ التحصيل', kind: 'DATE', column: 'paidAt' },
    ],
    measures: [
      COUNT,
      { key: 'amount', label: 'المحصَّل', kind: 'SUM', column: 'amount', format: 'MONEY' },
      { key: 'avgAmount', label: 'متوسط الدفعة', kind: 'AVG', column: 'amount', format: 'MONEY' },
    ],
    filters: [],
  },

  {
    key: 'EXPENSES',
    label: 'المصروفات',
    description: 'أين يذهب المال: بأي بند وإلى أي مورد ومن أي خزينة.',
    table: 'expenses',
    dateColumn: 'date',
    dimensions: [
      {
        key: 'ledgerAccount',
        label: 'بند المصروف',
        kind: 'REF',
        column: 'accountId',
        refTable: 'ledger_accounts',
        refLabel: 'name',
      },
      {
        key: 'supplier',
        label: 'المورد',
        kind: 'REF',
        column: 'supplierId',
        refTable: 'suppliers',
        refLabel: 'name',
      },
      {
        key: 'cashAccount',
        label: 'الخزينة',
        kind: 'REF',
        column: 'cashAccountId',
        refTable: 'cash_accounts',
        refLabel: 'name',
      },
      {
        key: 'billable',
        label: 'العميل المرتبط',
        kind: 'REF',
        column: 'billableAccountId',
        refTable: 'accounts',
        refLabel: 'name',
      },
      { key: 'date', label: 'التاريخ', kind: 'DATE', column: 'date' },
    ],
    measures: [
      COUNT,
      { key: 'amount', label: 'إجمالي المصروف', kind: 'SUM', column: 'amount', format: 'MONEY' },
      { key: 'avgAmount', label: 'متوسط المصروف', kind: 'AVG', column: 'amount', format: 'MONEY' },
    ],
    filters: [],
  },

  {
    key: 'BILLS',
    label: 'فواتير الموردين',
    description: 'ما عليك للموردين وتوزيعه عليهم وعلى حالات السداد.',
    table: 'bills',
    dateColumn: 'issuedAt',
    baseWhere: `status <> 'CANCELLED'`,
    dimensions: [
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: BILL_STATUS },
      {
        key: 'supplier',
        label: 'المورد',
        kind: 'REF',
        column: 'supplierId',
        refTable: 'suppliers',
        refLabel: 'name',
      },
      { key: 'issuedAt', label: 'تاريخ الفاتورة', kind: 'DATE', column: 'issuedAt' },
      { key: 'dueAt', label: 'تاريخ الاستحقاق', kind: 'DATE', column: 'dueAt' },
    ],
    measures: [
      COUNT,
      { key: 'total', label: 'الإجمالي', kind: 'SUM', column: 'total', format: 'MONEY' },
      { key: 'paid', label: 'المسدَّد', kind: 'SUM', column: 'paidAmount', format: 'MONEY' },
    ],
    filters: [
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: BILL_STATUS },
    ],
  },

  {
    key: 'ACTIVITIES',
    label: 'الأنشطة',
    description: 'حجم العمل الميداني: المكالمات والاجتماعات والمهام وتوزيعها.',
    table: 'activities',
    dateColumn: 'createdAt',
    dimensions: [
      { key: 'type', label: 'النوع', kind: 'ENUM', column: 'type', labels: ACTIVITY_TYPE },
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: ACTIVITY_STATUS },
      { key: 'priority', label: 'الأولوية', kind: 'ENUM', column: 'priority', labels: PRIORITY },
      owner('assigneeId', 'المكلَّف'),
      owner(),
      { key: 'createdAt', label: 'تاريخ الإنشاء', kind: 'DATE', column: 'createdAt' },
      { key: 'dueAt', label: 'تاريخ الاستحقاق', kind: 'DATE', column: 'dueAt' },
    ],
    measures: [
      COUNT,
      {
        key: 'duration',
        label: 'إجمالي الدقائق',
        kind: 'SUM',
        column: 'durationMin',
        format: 'NUMBER',
      },
    ],
    filters: [
      { key: 'type', label: 'النوع', kind: 'ENUM', column: 'type', labels: ACTIVITY_TYPE },
      { key: 'status', label: 'الحالة', kind: 'ENUM', column: 'status', labels: ACTIVITY_STATUS },
      {
        key: 'assigneeId',
        label: 'المكلَّف',
        kind: 'REF',
        column: 'assigneeId',
        refTable: 'users',
        refLabel: 'name',
      },
    ],
  },

  {
    key: 'ACCOUNTS',
    label: 'العملاء',
    description: 'قاعدة العملاء وتوزيعها على الأنشطة والمدن.',
    table: 'accounts',
    dateColumn: 'createdAt',
    dimensions: [
      { key: 'kind', label: 'النوع', kind: 'ENUM', column: 'kind', labels: ACCOUNT_KIND },
      { key: 'industry', label: 'النشاط', kind: 'TEXT', column: 'industry' },
      { key: 'city', label: 'المدينة', kind: 'TEXT', column: 'city' },
      { key: 'source', label: 'المصدر', kind: 'TEXT', column: 'source' },
      owner(),
      { key: 'createdAt', label: 'تاريخ الإضافة', kind: 'DATE', column: 'createdAt' },
    ],
    measures: [COUNT],
    filters: [{ key: 'kind', label: 'النوع', kind: 'ENUM', column: 'kind', labels: ACCOUNT_KIND }],
  },
] as const;

export const REPORT_SOURCE_MAP = Object.fromEntries(
  REPORT_SOURCES.map((s) => [s.key, s]),
) as Readonly<Record<string, SourceDef>>;

export const REPORT_SOURCE_KEYS = REPORT_SOURCES.map((s) => s.key);

/* ==========================================================================
   شكل التقرير ونتيجته
   ========================================================================== */

export interface ReportDefinition {
  source: string;
  dimension: string;
  measure: string;
  chart: ChartKind;
  grain?: DateGrain;
  /** نطاق زمني على العمود الزمني للمصدر. */
  from?: string | null;
  to?: string | null;
  filters?: Record<string, string>;
  /** عدد الصفوف المعروضة؛ الباقي يُجمَّع في صف "أخرى". */
  limit?: number;
  sort?: 'VALUE_DESC' | 'VALUE_ASC' | 'LABEL_ASC';
}

export interface ReportRow {
  key: string;
  label: string;
  value: number;
  share: number;
}

export interface ReportResult {
  rows: ReportRow[];
  total: number;
  /** عدد السجلات الداخلة في التقرير قبل التجميع. */
  records: number;
  format: ValueFormat;
  dimensionLabel: string;
  measureLabel: string;
  sourceLabel: string;
  generatedAt: string;
}

/** أهو تقسيم بالتاريخ، فيحتاج درجة زمنية. */
export function isDateDimension(source: string, dimension: string): boolean {
  const def = REPORT_SOURCE_MAP[source];
  return def?.dimensions.find((d) => d.key === dimension)?.kind === 'DATE';
}

/** تقارير جاهزة تُفتح بضغطة، وتصلح نقطة بداية للتعديل. */
export interface ReportPreset {
  key: string;
  label: string;
  description: string;
  definition: ReportDefinition;
}

export const REPORT_PRESETS: readonly ReportPreset[] = [
  {
    key: 'revenue-by-month',
    label: 'الإيراد المفوتر شهريًا',
    description: 'اتجاه الفوترة عبر الأشهر.',
    definition: {
      source: 'INVOICES',
      dimension: 'issuedAt',
      measure: 'total',
      chart: 'LINE',
      grain: 'MONTH',
    },
  },
  {
    key: 'leads-by-source',
    label: 'العملاء المحتملون حسب المصدر',
    description: 'أي قناة تجلب أكثر.',
    definition: { source: 'LEADS', dimension: 'source', measure: 'count', chart: 'BAR' },
  },
  {
    key: 'pipeline-by-stage',
    label: 'خط الأنابيب حسب المرحلة',
    description: 'أين تتكدّس القيمة.',
    definition: {
      source: 'DEALS',
      dimension: 'stage',
      measure: 'value',
      chart: 'BAR',
      filters: { result: 'OPEN' },
    },
  },
  {
    key: 'lost-reasons',
    label: 'أسباب خسارة الصفقات',
    description: 'ما يتكرر فقدانه ولماذا.',
    definition: {
      source: 'DEALS',
      dimension: 'lostReason',
      measure: 'value',
      chart: 'PIE',
      filters: { result: 'LOST' },
    },
  },
  {
    key: 'expenses-by-account',
    label: 'المصروفات حسب البند',
    description: 'أين يذهب المال.',
    definition: { source: 'EXPENSES', dimension: 'ledgerAccount', measure: 'amount', chart: 'PIE' },
  },
  {
    key: 'sales-by-rep',
    label: 'المبيعات حسب المندوب',
    description: 'من يحقق أكثر من الفواتير الصادرة.',
    definition: { source: 'INVOICES', dimension: 'owner', measure: 'total', chart: 'BAR' },
  },
  {
    key: 'collections-by-month',
    label: 'التحصيل شهريًا',
    description: 'ما دخل فعلًا لا ما فُوتر.',
    definition: {
      source: 'PAYMENTS',
      dimension: 'paidAt',
      measure: 'amount',
      chart: 'LINE',
      grain: 'MONTH',
    },
  },
  {
    key: 'calls-by-rep',
    label: 'المكالمات حسب المكلَّف',
    description: 'حجم النشاط الميداني لكل عضو.',
    definition: {
      source: 'ACTIVITIES',
      dimension: 'assigneeId',
      measure: 'count',
      chart: 'BAR',
      filters: { type: 'CALL' },
    },
  },
] as const;
